import { useTemplateContext } from "../provider";
import { VerificationSchemaView } from "../slices/types";
import { tabTitle } from "../utils";
import { Show } from "shared/components";
import { Tabs } from "../../../components/Tabs";
import { ApplicantType } from "shared/types";
import { ReactNode, useEffect } from "react";
import {
  basicInformationTabs,
  combinedIncomeTabs,
  criminalReportTabs,
  identityVerificationTabs,
  incomeHistoryTabs,
  countWalkInTabs,
  screeningServicesTabs,
  petScreeningReportTabs,
  VERIFICATIONS_WITH_TABS,
  housingHistoryTabs,
  rentalVerificationTabs,
  boomFinanceTabs
} from "../constant";
import { cx } from "../../../../utils/classnames";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { SwitchWithReadOnlyMode } from "./SwitchWithReadOnlyMode";
import { getApplicantRole } from "utils/getApplicantRole";

const HEADER_CONTENT = {
  [ApplicantType.Applicant]: {
    title: "Applicant application",
    description:
      "Applicant is an individual over 18 living in the property, like a roommate, spouse, or partner."
  },
  [ApplicantType.CoSigner]: {
    title: "Guarantor application",
    description:
      "Guarantor is someone who won’t live in the property but will provide financial support."
  },
  [ApplicantType.Occupant]: {
    title: "Occupant application",
    description:
      "Occupant is someone who lives in the property but isn’t financially responsible for the lease."
  },
  [VerificationSchemaView.TenantQualifications]: {
    title: tabTitle[VerificationSchemaView.TenantQualifications],
    description:
      "Specify any resident qualifications or information for applicants and guarantors to see on the first page."
  },
  [VerificationSchemaView.SubmissionDisclosure]: {
    title: tabTitle[VerificationSchemaView.SubmissionDisclosure],
    description:
      "Is there specific information for the applicant or guarantor? It will appear before submitting the application."
  },
  [VerificationSchemaView.CountyWalkIn]: {
    title: "County criminal record search"
  },
  [VerificationSchemaView.AdverseAction]: {
    title: "Select adverse action template",
    description:
      "Adverse action notice will be sent to applicants when an application is declined or conditionally approved based on obtained information"
  },
  [VerificationSchemaView.DiscountCodes]: {
    title: tabTitle[VerificationSchemaView.DiscountCodes],
    description:
      "Share discount codes with applicants so they can apply them during checkout to reduce their application fee"
  },
  [VerificationSchemaView.AdverseActionGenericTemplate]: {
    title: "Generic template"
  },
  [VerificationSchemaView.AdverseActionWATemplate]: {
    title: "WA template"
  }
};

const HEADER_CRUMBS = {
  [VerificationSchemaView.AdverseActionGenericTemplate]: [
    {
      title: "Adverse action",
      verificationSchemaView: VerificationSchemaView.AdverseAction
    },
    {
      title:
        HEADER_CONTENT[VerificationSchemaView.AdverseActionGenericTemplate]
          .title,
      verificationSchemaView:
        VerificationSchemaView.AdverseActionGenericTemplate
    }
  ],
  [VerificationSchemaView.AdverseActionWATemplate]: [
    {
      title: "Adverse action",
      verificationSchemaView: VerificationSchemaView.AdverseAction
    },
    {
      title:
        HEADER_CONTENT[VerificationSchemaView.AdverseActionWATemplate].title,
      verificationSchemaView: VerificationSchemaView.AdverseActionWATemplate
    }
  ]
};

const getHeaderCrumbs = (
  setSelectedVerificationSchema: (
    selectedVerification: VerificationSchemaView
  ) => void,
  selectedVerificationSchema: VerificationSchemaView,
  partnerType: ApplicantType
) => {
  return (
    HEADER_CRUMBS[selectedVerificationSchema]?.map((crumb) => ({
      title: crumb.title,
      onClick: () =>
        setSelectedVerificationSchema(crumb.verificationSchemaView),
      isActive: selectedVerificationSchema === crumb.verificationSchemaView
    })) || [
      {
        title: `${getApplicantRole(partnerType)} application`,
        onClick: () =>
          setSelectedVerificationSchema(
            VerificationSchemaView.ScreeningServices
          )
      },
      {
        title:
          getHeader(selectedVerificationSchema, partnerType)?.title ||
          tabTitle[selectedVerificationSchema],
        isActive: true,
        onClick: () => setSelectedVerificationSchema(selectedVerificationSchema)
      }
    ]
  );
};

const getHeader = (
  verificationType: VerificationSchemaView,
  applicantType: ApplicantType
) => {
  if (verificationType === VerificationSchemaView.ScreeningServices)
    return HEADER_CONTENT[applicantType];

  return (
    HEADER_CONTENT[verificationType] || {
      title:
        VERIFICATIONS_WITH_TABS.find(({ tabs }) =>
          tabs.includes(verificationType)
        )?.title || tabTitle[verificationType],
      description: ""
    }
  );
};

export const LayoutHeader = () => {
  const {
    partnerType,
    selectedVerificationSchema,
    setSelectedVerificationSchema,
    setSelectedStep,
    isModal,
    template,
    savedTemplate,
    setTemplate,
    readOnly
  } = useTemplateContext();
  const { title, description } = getHeader(
    selectedVerificationSchema,
    partnerType
  );

  const headerCrumbs = getHeaderCrumbs(
    setSelectedVerificationSchema,
    selectedVerificationSchema,
    partnerType
  );

  const getDisplayMap = (
    tabs: VerificationSchemaView[],
    defaultLabel: VerificationSchemaView | null
  ): Record<VerificationSchemaView, ReactNode> =>
    tabs.reduce<Record<VerificationSchemaView, ReactNode>>(
      (acc, schema) => ({
        ...acc,
        [schema]: schema === defaultLabel ? "General" : tabTitle[schema]
      }),
      {} as Record<VerificationSchemaView, ReactNode>
    );

  const tabSections = [
    { tabs: basicInformationTabs, label: null, setActiveTab: setSelectedStep },
    {
      tabs: identityVerificationTabs,
      label: VerificationSchemaView.IdentityVerification
    },
    {
      tabs: petScreeningReportTabs,
      label: VerificationSchemaView.PetScreeningReport
    },
    {
      tabs: boomFinanceTabs,
      label: VerificationSchemaView.BoomFinanceVerification
    },
    {
      tabs: incomeHistoryTabs,
      label: VerificationSchemaView.IncomeHistory,
      setActiveTab: setSelectedStep,
      startedTab: 3
    },
    {
      tabs: combinedIncomeTabs,
      label: VerificationSchemaView.CombinedIncomeVerification,
      setActiveTab: setSelectedStep,
      startedTab: 0
    },
    {
      tabs: housingHistoryTabs,
      label: VerificationSchemaView.HousingHistoryGeneralConfiguration
    },
    {
      tabs: rentalVerificationTabs,
      label: VerificationSchemaView.RentalVerification
    },
    { tabs: criminalReportTabs, label: VerificationSchemaView.CriminalReport },
    {
      tabs: countWalkInTabs,
      label: VerificationSchemaView.CountyWalkIn
    }
  ];

  useEffect(() => {
    // Reset step when switching to Screening Services
    if (
      selectedVerificationSchema === VerificationSchemaView.ScreeningServices ||
      selectedVerificationSchema === VerificationSchemaView.CountyWalkIn
    )
      setSelectedStep(0);
  }, [selectedVerificationSchema]);

  const togglePartnerType = () => {
    if (!template || !savedTemplate) return;

    setTemplate({
      ...template,
      settings: {
        ...template.settings,
        [partnerType]: {
          ...template.settings[partnerType],
          enabled_screening_services:
            !template.settings[partnerType]?.enabled_screening_services
        }
      },
      verification_schemas: {
        ...template.verification_schemas,
        [partnerType]: {
          ...savedTemplate.verification_schemas.applicant
        }
      }
    });
  };

  const shouldShowBreadcrumbs =
    [
      VerificationSchemaView.CreditReport,
      VerificationSchemaView.AdverseActionGenericTemplate,
      VerificationSchemaView.AdverseActionWATemplate
    ].includes(selectedVerificationSchema) ||
    screeningServicesTabs.includes(selectedVerificationSchema);

  return (
    <div
      className={cx(
        screeningServicesTabs.includes(selectedVerificationSchema)
          ? ""
          : "border-b border-alpha-100",
        "w-full"
      )}
    >
      <Show if={shouldShowBreadcrumbs}>
        <div className="px-6 py-[22px] border-b border-alpha-40 bg-background-1000">
          <BreadCrumbs crumbs={headerCrumbs} />
        </div>
      </Show>

      <div className="bg-background-1000 md:px-8 px-4">
        <div className="md:py-6 py-3">
          <div className="flex justify-between items-center">
            <div className="space-y-2">
              <h3 className="font-bold text-base md:text-xl">{title}</h3>
              {description ? (
                <p className="text-content-400 text-xs md:text-base">
                  {description}
                </p>
              ) : null}
            </div>

            <Show
              if={
                selectedVerificationSchema ===
                  VerificationSchemaView.ScreeningServices &&
                [ApplicantType.CoSigner, ApplicantType.Occupant].includes(
                  partnerType
                )
              }
            >
              <SwitchWithReadOnlyMode
                label={
                  partnerType === ApplicantType.CoSigner
                    ? "Allow guarantors"
                    : "Allow occupants"
                }
                checked={Boolean(
                  template?.settings[partnerType]?.enabled_screening_services
                )}
                onChange={() => togglePartnerType()}
                readOnly={readOnly}
              />
            </Show>
          </div>
        </div>

        <div
          className={cx(
            isModal ? "max-w-[calc(90vw-312px)]" : "max-w-[calc(100vw-370px)]"
          )}
        >
          {tabSections.map(
            ({
              tabs,
              label,
              setActiveTab = setSelectedVerificationSchema,
              startedTab
            }) => (
              <Show
                key={tabs.join(",")}
                if={tabs.includes(selectedVerificationSchema)}
              >
                <Tabs
                  variant="underline"
                  tabs={tabs}
                  displayMap={getDisplayMap(tabs, label)}
                  selected={selectedVerificationSchema}
                  setActiveTab={(t) => {
                    if (
                      setActiveTab === setSelectedStep &&
                      t !== VerificationSchemaView.AdditionalQuestions
                    )
                      setSelectedStep(
                        tabs.findIndex((schema) => schema === t) -
                          (startedTab ?? 0)
                      );

                    setSelectedVerificationSchema(t as VerificationSchemaView);
                  }}
                />
              </Show>
            )
          )}
        </div>
      </div>
    </div>
  );
};
